import { useCallback, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { BadgeCheck, Check, ShieldOff, Store, X } from 'lucide-react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, RowAction } from '@/components/DataTable/types'
import { fetchSellerApplications, decideSeller, verifySeller, type AdminSellerRow } from '@/lib/adapters'
import { PERMISSIONS } from '@/lib/permissions'
import { StatusBadge, type StatusKind } from '@/components/StatusBadge'
import { RelativeTime } from '@/components/DataDisplay'
import { DangerDialog } from '@/components/DangerDialog'
import { useToast } from '@/providers/UiProviders'

/**
 * A21.02 — Sotuvchi arizalari (Bozor / spec-29 §3). GET /admin/market/sellers;
 * tasdiq/rad → POST /admin/market/sellers/:id/decide {approve, reason}. Tasdiqlangan
 * sotuvchigina e'lon joylay oladi. Ishonchli-belgi (verified) toggle ham shu yerda.
 */
export function SellerApplications() {
  const t = useT()
  const { toast } = useToast()
  const [rejectTarget, setRejectTarget] = useState<AdminSellerRow | null>(null)
  const [reloadKey, setReloadKey] = useState(0)

  const nameOf = (r: AdminSellerRow) => r.name || (r.username ? `@${r.username}` : `#${r.user_id}`)

  const run = useCallback(
    async (fn: () => Promise<unknown>) => {
      try {
        await fn()
        toast(t.market.done, { tone: 'success' })
        setReloadKey((k) => k + 1)
      } catch (e) {
        toast(t.market.failed, {
          tone: 'error',
          ...(e instanceof Error && e.message ? { description: e.message } : {}),
        })
        throw e
      }
    },
    [toast, t],
  )

  const statusKind = (s: string): StatusKind =>
    s === 'approved' ? 'active' : s === 'rejected' ? 'deleted' : s === 'suspended' ? 'banned' : 'pending'
  const statusLabel = (s: string) =>
    s === 'approved'
      ? t.market.approved
      : s === 'rejected'
        ? t.market.rejected
        : s === 'suspended'
          ? t.market.suspended
          : t.market.pending

  const columns = useMemo<AdminColumn<AdminSellerRow>[]>(
    () => [
      {
        key: 'shop_name',
        header: t.market.shop,
        type: 'text',
        minWidth: 200,
        render: (r) => (
          <div className="min-w-0">
            <Link to={`/users/${r.user_id}`} className="block truncate font-medium text-accent hover:underline">
              {r.shop_name || '—'}
            </Link>
            <span className="block truncate text-xs text-secondary">{nameOf(r)}</span>
          </div>
        ),
      },
      {
        key: 'status',
        header: t.market.status,
        type: 'enum',
        width: 150,
        filterable: true,
        options: [
          { value: 'pending', label: t.market.pending, status: 'pending' },
          { value: 'approved', label: t.market.approved, status: 'active' },
          { value: 'rejected', label: t.market.rejected, status: 'deleted' },
          { value: 'suspended', label: t.market.suspended, status: 'banned' },
        ],
        render: (r) => <StatusBadge status={statusKind(r.status)} label={statusLabel(r.status)} />,
      },
      { key: 'region', header: t.market.region, type: 'text', width: 140 },
      {
        key: 'rating',
        header: t.market.rating,
        type: 'number',
        width: 90,
        align: 'right',
        render: (r) => <span className="block text-right tabular">{r.rating > 0 ? r.rating.toFixed(1) : '—'}</span>,
      },
      { key: 'deals_done', header: t.market.deals, type: 'number', width: 90, align: 'right' },
      {
        key: 'verified',
        header: t.market.verified,
        type: 'text',
        width: 100,
        render: (r) => (r.verified ? <BadgeCheck size={16} className="text-accent" /> : <span className="text-tertiary">—</span>),
      },
      {
        key: 'created_at',
        header: t.market.created,
        type: 'date',
        width: 150,
        render: (r) => <RelativeTime value={r.created_at} className="text-secondary" />,
      },
    ],
    [t],
  )

  const rowActions = useMemo<RowAction<AdminSellerRow>[]>(
    () => [
      {
        id: 'approve',
        label: t.market.approve,
        icon: <Check size={13} />,
        visible: (r) => r.status !== 'approved',
        onSelect: (r) => void run(() => decideSeller(r.user_id, true, '')),
      },
      {
        id: 'reject',
        label: t.market.reject,
        icon: <X size={13} />,
        danger: true,
        visible: (r) => r.status === 'pending',
        onSelect: (r) => setRejectTarget(r),
      },
      {
        id: 'verify',
        label: t.market.verify,
        icon: <BadgeCheck size={13} />,
        visible: (r) => r.status === 'approved' && !r.verified,
        onSelect: (r) => void run(() => verifySeller(r.user_id, true)),
      },
      {
        id: 'unverify',
        label: t.market.unverify,
        icon: <ShieldOff size={13} />,
        visible: (r) => r.verified,
        onSelect: (r) => void run(() => verifySeller(r.user_id, false)),
      },
    ],
    [t, run],
  )

  return (
    <>
      <PageHeader
        title={t.market.sellerApps}
        screenId="MKT.01"
        actions={
          <Link to="/market/categories" className="text-sm text-accent hover:underline">
            {t.market.categories}
          </Link>
        }
      />
      <DataTable<AdminSellerRow>
        key={reloadKey}
        screenId="MKT.01"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetchSellerApplications}
        rowId={(r) => String(r.user_id)}
        rowActions={rowActions}
        emptyState={{ icon: <Store size={22} />, title: t.market.sellerApps }}
      />

      {rejectTarget && (
        <DangerDialog
          open
          onClose={() => setRejectTarget(null)}
          screenId="MKT.01"
          level={2}
          title={`${t.market.reject} — ${rejectTarget.shop_name || nameOf(rejectTarget)}`}
          description={t.market.rejectPrompt}
          confirmLabel={t.market.reject}
          onConfirm={async ({ reason }) => {
            await run(() => decideSeller(rejectTarget.user_id, false, reason))
            setRejectTarget(null)
          }}
        />
      )}
    </>
  )
}
